(() => {
  const PENDING_PACKAGE_KEY = "stars-cars:pending-package:v1";
  const EASY_GROUP_SELECTOR = ".et-product-page-options .et-option-group";

  if (customElements.get("stars-cars-ticket-map")) return;

  class StarsCarsTicketMap extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.sheet = this.querySelector("[data-ticket-sheet]");
      this.dialog = this.querySelector("[data-sheet-dialog]");
      this.panels = Array.from(this.querySelectorAll("[data-ticket-panel]"));
      this.openers = Array.from(this.querySelectorAll("[data-open-tier]"));
      this.closeTimer = null;
      this.lastOpener = null;
      this.activePanel = null;
      this.passengerRenderToken = 0;

      if (!this.sheet || !this.dialog || this.panels.length === 0) return;

      document.body.classList.add("sc-map-easy-active");
      this.nativeForm = this.findNativeProductForm();
      this.hideNativeProductSection();

      this.handleKeydown = this.onKeydown.bind(this);
      document.addEventListener("keydown", this.handleKeydown);

      this.openers.forEach((opener) => {
        opener.addEventListener("click", () => this.open(opener.dataset.openTier, opener));
      });

      this.querySelectorAll("[data-sheet-close]").forEach((closer) => {
        closer.addEventListener("click", () => this.close());
      });

      this.querySelectorAll("[data-add-ticket]").forEach((button) => {
        button.addEventListener("click", () => this.continueToEasy(button));
      });
    }

    disconnectedCallback() {
      if (this.handleKeydown) document.removeEventListener("keydown", this.handleKeydown);
      window.clearTimeout(this.closeTimer);

      if (!document.querySelector("stars-cars-ticket-map")) {
        document.body.classList.remove("sc-map-easy-active");
      }

      if (!document.querySelector(".sc-map__sheet.is-open")) {
        document.documentElement.classList.remove("sc-map-sheet-open");
      }
    }

    get variantIds() {
      return this.panels.map((panel) => panel.dataset.ticketVariantId).filter(Boolean);
    }

    findNativeProductForm() {
      const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
      const mainProductForm = forms.find(
        (form) => form.dataset.eventContext === "MainProductPage",
      );

      if (mainProductForm) return mainProductForm;

      const knownVariantIds = new Set(this.variantIds.map(String));
      return (
        forms.find((form) =>
          Array.from(form.querySelectorAll('[name="id"]')).some((input) =>
            knownVariantIds.has(String(input.value)),
          ),
        ) || null
      );
    }

    hideNativeProductSection() {
      if (!this.nativeForm) return;
      const sectionHost = this.nativeForm.closest(".shopify-section");
      if (sectionHost && !sectionHost.contains(this)) {
        sectionHost.classList.add("sc-map-native-product-host");
        sectionHost.setAttribute("aria-hidden", "true");
      }
    }

    restoreNativeProductSection() {
      if (!this.nativeForm) return;
      const sectionHost = this.nativeForm.closest(".shopify-section");
      if (!sectionHost) return;
      sectionHost.classList.remove("sc-map-native-product-host");
      sectionHost.removeAttribute("aria-hidden");
    }

    open(tierId, opener) {
      const panel = this.panels.find((item) => item.dataset.ticketPanel === tierId);
      if (!panel) return;

      window.clearTimeout(this.closeTimer);
      this.lastOpener = opener;
      this.activePanel = panel;
      this.resetPanel(panel);

      this.panels.forEach((item) => {
        item.hidden = item !== panel;
      });

      this.openers.forEach((item) => {
        item.setAttribute("aria-pressed", item.dataset.openTier === tierId ? "true" : "false");
      });

      this.dialog.setAttribute("aria-label", `${panel.dataset.ticketTitle} ticket options`);
      this.sheet.hidden = false;
      this.sheet.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("sc-map-sheet-open");

      window.requestAnimationFrame(() => {
        this.sheet.classList.add("is-open");
        this.sheet.querySelector(".sc-map__close")?.focus();
      });

      this.renderPassengerOptions(panel);
    }

    close(restoreFocus = true) {
      if (this.sheet.hidden) return;

      this.sheet.classList.remove("is-open");
      this.sheet.setAttribute("aria-hidden", "true");
      this.openers.forEach((item) => item.setAttribute("aria-pressed", "false"));

      this.closeTimer = window.setTimeout(() => {
        this.sheet.hidden = true;
      }, 280);

      if (!document.querySelector(".sc-map__sheet.is-open")) {
        document.documentElement.classList.remove("sc-map-sheet-open");
      }

      if (restoreFocus && this.lastOpener && document.contains(this.lastOpener)) {
        this.lastOpener.focus();
      }
    }

    resetPanel(panel) {
      const passengerContainer = panel.querySelector("[data-passenger-options]");
      const total = panel.querySelector("[data-selected-total]");
      const addButton = panel.querySelector("[data-add-ticket]");
      const error = panel.querySelector("[data-ticket-error]");

      if (passengerContainer) {
        passengerContainer.innerHTML = "";
        const loading = document.createElement("p");
        loading.className = "sc-map__options-status";
        loading.textContent = this.dataset.loadingOptionsText || "Loading passenger options…";
        passengerContainer.appendChild(loading);
      }

      if (total) total.textContent = this.dataset.selectPassengersText || "Select passengers";

      if (addButton) {
        addButton.disabled = true;
        addButton.removeAttribute("aria-busy");
        addButton.textContent = addButton.dataset.addLabel;
        delete addButton.dataset.addonVariantId;
        delete addButton.dataset.easyOptionValue;
        delete addButton.dataset.passengerTitle;
      }

      if (error) error.textContent = "";
    }

    normalize(value) {
      return String(value || "")
        .replace(/\*/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    }

    findEasyPassengerGroup() {
      const groups = Array.from(document.querySelectorAll(EASY_GROUP_SELECTOR));
      if (!groups.length) return null;

      const expectedTitle = this.normalize(this.dataset.easyPassengerGroupTitle || "Passengers");
      const titleMatch = groups.find((group) => {
        const heading = Array.from(group.children).find((child) => child.tagName === "P");
        return this.normalize(heading?.textContent) === expectedTitle;
      });

      if (titleMatch) return titleMatch;

      return (
        groups.find((group) =>
          Array.from(group.querySelectorAll('input[type="radio"]')).some((radio) => {
            const title = this.normalize(this.readEasyOptionTitle(radio));
            return title === "just me" || title.includes("passenger");
          }),
        ) || null
      );
    }

    waitForEasyPassengerGroup(timeout = 12000) {
      const existing = this.findEasyPassengerGroup();
      if (existing) return Promise.resolve(existing);

      return new Promise((resolve) => {
        let settled = false;
        let observer = null;
        let timeoutId = null;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          observer?.disconnect();
          window.clearTimeout(timeoutId);
          resolve(value);
        };

        observer = new MutationObserver(() => {
          const group = this.findEasyPassengerGroup();
          if (group) finish(group);
        });

        observer.observe(document.documentElement, { childList: true, subtree: true });
        timeoutId = window.setTimeout(() => finish(null), timeout);
      });
    }

    readEasyOptionTitle(radio) {
      const label = radio.closest("label");
      const left = label?.querySelector("div");
      const titleNode = left
        ? Array.from(left.querySelectorAll("span")).find(
            (span) =>
              !span.classList.contains("et-opt-unavailable") &&
              !span.classList.contains("et-opt-dateso"),
          )
        : null;

      return titleNode?.textContent?.trim() || radio.value || "Passenger option";
    }

    readEasyOptionPriceLabel(radio) {
      const label = radio.closest("label");
      const directPrice = label
        ? Array.from(label.children).find((child) => child.tagName === "SPAN")
        : null;

      if (directPrice?.textContent?.trim()) return directPrice.textContent.trim();

      const price = Number(radio.dataset.optionPrice || 0);
      return price > 0 ? `+${this.formatMoney(Math.round(price * 100))}` : "Free";
    }

    async renderPassengerOptions(panel) {
      const renderToken = ++this.passengerRenderToken;
      const container = panel.querySelector("[data-passenger-options]");
      const addButton = panel.querySelector("[data-add-ticket]");
      if (!container || !addButton) return;

      const group = await this.waitForEasyPassengerGroup();
      if (renderToken !== this.passengerRenderToken || this.activePanel !== panel) return;

      if (!group) {
        this.restoreNativeProductSection();
        container.innerHTML = "";
        const message = document.createElement("p");
        message.className = "sc-map__options-status";
        message.textContent =
          this.dataset.optionsErrorText ||
          "Passenger options are unavailable. Refresh the page and try again.";
        container.appendChild(message);
        return;
      }

      const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
      radios.forEach((radio) => {
        radio.checked = false;
      });

      container.innerHTML = "";

      radios.forEach((radio) => {
        const title = this.readEasyOptionTitle(radio);
        const button = document.createElement("button");
        button.type = "button";
        button.className = "sc-map__passenger-choice";
        button.dataset.easyOptionValue = radio.value;
        button.dataset.addonVariantId = radio.dataset.variantId || "";
        button.dataset.optionPrice = radio.dataset.optionPrice || "0";
        button.dataset.passengerTitle = title;
        button.setAttribute("aria-pressed", "false");

        const isUnavailable = radio.disabled || !radio.dataset.variantId;
        if (isUnavailable) {
          button.disabled = true;
          button.setAttribute("aria-disabled", "true");
        }

        const titleNode = document.createElement("span");
        titleNode.textContent = title;
        const priceNode = document.createElement("small");
        priceNode.textContent = isUnavailable
          ? radio.closest("label")?.querySelector(".et-opt-unavailable, .et-opt-dateso")?.textContent ||
            "Unavailable"
          : this.readEasyOptionPriceLabel(radio);

        button.append(titleNode, priceNode);
        button.addEventListener("click", () => this.selectPassengerChoice(button));
        container.appendChild(button);
      });

      if (!container.children.length) {
        const message = document.createElement("p");
        message.className = "sc-map__options-status";
        message.textContent = this.dataset.optionsErrorText || "Passenger options are unavailable.";
        container.appendChild(message);
      }
    }

    selectPassengerChoice(choice) {
      if (choice.disabled || !this.activePanel) return;

      const panel = choice.closest("[data-ticket-panel]");
      const total = panel.querySelector("[data-selected-total]");
      const addButton = panel.querySelector("[data-add-ticket]");
      const error = panel.querySelector("[data-ticket-error]");
      const synced = this.syncEasyPassengerChoice(
        choice.dataset.easyOptionValue,
        choice.dataset.addonVariantId,
      );

      if (!synced) {
        if (error) error.textContent = this.dataset.optionsErrorText || "Passenger options are unavailable.";
        return;
      }

      panel.querySelectorAll(".sc-map__passenger-choice").forEach((item) => {
        item.setAttribute("aria-pressed", item === choice ? "true" : "false");
      });

      const basePrice = Number(panel.dataset.ticketPrice || 0);
      const addonPrice = Math.round(Number(choice.dataset.optionPrice || 0) * 100);
      if (total) total.textContent = this.formatMoney(basePrice + addonPrice);
      if (error) error.textContent = "";

      if (addButton) {
        addButton.dataset.addonVariantId = choice.dataset.addonVariantId;
        addButton.dataset.easyOptionValue = choice.dataset.easyOptionValue;
        addButton.dataset.passengerTitle = choice.dataset.passengerTitle;
        addButton.disabled = panel.dataset.ticketAvailable !== "true";
      }
    }

    syncEasyPassengerChoice(optionValue, addonVariantId) {
      const group = this.findEasyPassengerGroup();
      if (!group) return false;

      const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
      const selected = radios.find(
        (radio) =>
          String(radio.value) === String(optionValue) &&
          String(radio.dataset.variantId || "") === String(addonVariantId || ""),
      );

      if (!selected || selected.disabled || !selected.dataset.variantId) return false;

      radios.forEach((radio) => {
        radio.checked = radio === selected;
      });

      selected.dispatchEvent(new Event("input", { bubbles: true }));
      selected.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || window.Shopify?.currency?.active || "USD";
      const locale = document.documentElement.lang || "en-US";

      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency,
        }).format(Number(cents || 0) / 100);
      } catch (_error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    setNativeVariant(form, variantId) {
      const controls = Array.from(form.querySelectorAll('[name="id"]'));
      if (!controls.length) return false;

      let matched = false;
      controls.forEach((control) => {
        if (control.tagName === "SELECT") {
          const hasOption = Array.from(control.options).some(
            (option) => String(option.value) === String(variantId),
          );
          if (hasOption) {
            control.value = String(variantId);
            matched = true;
          }
        } else if (control.type === "radio") {
          control.checked = String(control.value) === String(variantId);
          if (control.checked) matched = true;
        } else {
          control.value = String(variantId);
          control.disabled = false;
          matched = true;
        }
      });

      const changedControl = controls.find((control) =>
        control.type === "radio" ? control.checked : String(control.value) === String(variantId),
      );
      changedControl?.dispatchEvent(new Event("input", { bubbles: true }));
      changedControl?.dispatchEvent(new Event("change", { bubbles: true }));
      return matched;
    }

    forceQuantityOne(form) {
      const quantityControls = Array.from(
        form.querySelectorAll('input[name="quantity"], select[name="quantity"]'),
      );

      quantityControls.forEach((control) => {
        control.value = "1";
        control.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    async fetchCart() {
      const shopRoot = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${shopRoot}cart.js`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (!response.ok) throw new Error("The cart could not be prepared.");
      return response.json();
    }

    createGroupId() {
      const randomPart = window.crypto?.getRandomValues
        ? Array.from(window.crypto.getRandomValues(new Uint32Array(2)))
            .map((value) => value.toString(36))
            .join("")
        : Math.random().toString(36).slice(2, 12);

      return `sc-${Date.now().toString(36)}-${randomPart}`;
    }

    savePendingPackage(panel, button, cart) {
      const pending = {
        version: 1,
        createdAt: Date.now(),
        groupId: this.createGroupId(),
        ticketProductId: Number(this.dataset.ticketProductId || 0),
        ticketVariantId: Number(panel.dataset.ticketVariantId || 0),
        addonVariantId: Number(button.dataset.addonVariantId || 0),
        ticketTitle: panel.dataset.ticketTitle || panel.dataset.ticketVariantTitle || "Stars & Cars",
        passengerTitle: button.dataset.passengerTitle || "Passengers",
        baselineItems: (cart.items || []).map((item) => ({
          key: item.key,
          variantId: Number(item.variant_id),
          quantity: Number(item.quantity),
        })),
      };

      sessionStorage.setItem(PENDING_PACKAGE_KEY, JSON.stringify(pending));
      return pending;
    }

    watchEasyCancellation(overlay) {
      overlay.addEventListener(
        "click",
        (event) => {
          const target = event.target instanceof Element ? event.target : null;
          const isCancellation =
            event.target === overlay ||
            target?.closest(".et-close, .et-btn-secondary, .et-popup-cancel");

          if (!isCancellation) return;

          try {
            sessionStorage.removeItem(PENDING_PACKAGE_KEY);
          } catch (_storageError) {
            // The next map submission can safely replace the pending package.
          }
        },
        { capture: true },
      );
    }

    watchForEasyOverlay(panel, button) {
      let settled = false;
      let observer = null;
      let timeoutId = null;
      const finish = (easyOverlay) => {
        if (settled) return;
        settled = true;
        observer?.disconnect();
        window.clearTimeout(timeoutId);

        if (easyOverlay) {
          this.watchEasyCancellation(easyOverlay);
          return;
        }

        try {
          sessionStorage.removeItem(PENDING_PACKAGE_KEY);
        } catch (_storageError) {
          // The shopper-facing error below is enough.
        }

        this.open(panel.dataset.ticketPanel, this.lastOpener || button);
        const reopenedError = panel.querySelector("[data-ticket-error]");
        if (reopenedError) {
          reopenedError.textContent =
            this.dataset.triggerErrorText || "The attendee form could not be opened.";
        }
      };

      const existingOverlay = document.querySelector(".et-overlay");
      if (existingOverlay) {
        finish(existingOverlay);
        return;
      }

      observer = new MutationObserver(() => {
        const easyOverlay = document.querySelector(".et-overlay");
        if (easyOverlay) finish(easyOverlay);
      });
      observer.observe(document.body, { childList: true, subtree: true });
      timeoutId = window.setTimeout(() => finish(null), 8000);
    }

    async continueToEasy(button) {
      if (button.getAttribute("aria-busy") === "true") return;

      const panel = button.closest("[data-ticket-panel]");
      const error = panel?.querySelector("[data-ticket-error]");
      const ticketVariantId = Number(panel?.dataset.ticketVariantId || 0);
      const addonVariantId = Number(button.dataset.addonVariantId || 0);

      if (!panel || !ticketVariantId || !addonVariantId) {
        if (error) error.textContent = this.dataset.selectPassengersText || "Select passengers.";
        return;
      }

      if (panel.dataset.ticketAvailable !== "true") {
        if (error) error.textContent = "This ticket type is currently unavailable.";
        return;
      }

      this.nativeForm = this.findNativeProductForm();
      const nativeButton = this.nativeForm?.querySelector(
        '[type="submit"][name="add"], button[type="submit"], input[type="submit"]',
      );

      if (!this.nativeForm || !nativeButton || !this.findEasyPassengerGroup()) {
        if (error) error.textContent = this.dataset.triggerErrorText || "The attendee form could not be opened.";
        return;
      }

      if (!this.setNativeVariant(this.nativeForm, ticketVariantId)) {
        if (error) error.textContent = this.dataset.triggerErrorText || "The attendee form could not be opened.";
        return;
      }

      this.forceQuantityOne(this.nativeForm);

      const passengerSynced = this.syncEasyPassengerChoice(
        button.dataset.easyOptionValue,
        button.dataset.addonVariantId,
      );

      if (!passengerSynced) {
        if (error) error.textContent = this.dataset.optionsErrorText || "Passenger options are unavailable.";
        return;
      }

      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.textContent = this.dataset.openingText || "Opening attendee form…";
      if (error) error.textContent = "";

      try {
        const cart = await this.fetchCart();
        this.savePendingPackage(panel, button, cart);

        nativeButton.disabled = false;
        nativeButton.removeAttribute("aria-disabled");
        this.close(false);

        document.dispatchEvent(
          new CustomEvent("stars-cars:easy-handoff", {
            bubbles: true,
            detail: { ticketVariantId, addonVariantId },
          }),
        );

        this.watchForEasyOverlay(panel, button);
        nativeButton.click();
      } catch (requestError) {
        if (error) {
          error.textContent =
            requestError?.message || "The cart could not be prepared. Refresh the page and try again.";
        }
        button.disabled = false;
        button.removeAttribute("aria-busy");
        button.textContent = button.dataset.addLabel;
      }
    }

    onKeydown(event) {
      if (this.sheet.hidden) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        this.dialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((item) => !item.closest("[hidden]"));

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define("stars-cars-ticket-map", StarsCarsTicketMap);
})();
